import { Component, ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    // ARCH-06: report to Sentry too (a no-op when Sentry.init never ran).
    Sentry.captureReactException(error, errorInfo)
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const isDev = import.meta.env.DEV

    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <h1 className="text-xl font-bold tracking-tight" style={{ color: '#0F1419' }}>
            LendSolo
          </h1>
          <p className="text-xs text-gray-500 mb-8">Loan Management</p>

          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>

          <h2 className="text-lg font-semibold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-6">
            We hit an unexpected error. Try reloading the page.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Reload Page
            </button>
            <a
              href="/dashboard"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
            >
              Go to Dashboard
            </a>
          </div>

          {isDev && this.state.error && (
            <details className="mt-8 text-left">
              <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">
                Error Details (dev only)
              </summary>
              <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-red-700 overflow-x-auto whitespace-pre-wrap break-words border border-gray-200">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
